import { Moon, Sun } from "lucide-react";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useTheme } from "next-themes";
import { useTranslation } from "react-i18next";
import { UI_LANGUAGES } from "../../i18n/languages";
import { LanguageFlagIcon } from "./ui/language-flag-icon";

const emptySubscribe = () => () => {};

export function LoginStagePrefs() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const mounted = useSyncExternalStore(emptySubscribe, () => true, () => false);
  const [langOpen, setLangOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const currentLang = useMemo(
    () => UI_LANGUAGES.find((item) => item.lng === i18n.resolvedLanguage) ?? UI_LANGUAGES[0],
    [i18n.resolvedLanguage]
  );

  useEffect(() => {
    if (!langOpen) {
      return;
    }
    const close = () => setLangOpen(false);
    const onPointerDown = (event: PointerEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        close();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [langOpen]);

  const isDark = theme === "dark";

  return (
    <div ref={boxRef} className="absolute top-4 right-4 z-30 md:top-6 md:right-6">
      <div className="flex items-center gap-2">
        {mounted ? (
          <button
            type="button"
            onClick={() => setTheme(isDark ? "light" : "dark")}
            className="flex size-[34px] cursor-pointer items-center justify-center rounded-full border border-[var(--lg-line)] bg-[var(--lg-rail)] text-[var(--lg-ink)] transition-colors duration-200 hover:border-[var(--lg-mint)]/50 hover:text-[var(--lg-mint)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lg-mint)]"
            aria-label={t("layout.toggleDark")}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        ) : (
          <span className="size-[34px]" aria-hidden />
        )}
        <button
          type="button"
          onClick={() => setLangOpen((open) => !open)}
          className="flex h-[34px] cursor-pointer items-center justify-center gap-2 rounded-full border border-[var(--lg-line)] bg-[var(--lg-rail)] px-3 text-[var(--lg-ink)] transition-colors duration-200 hover:border-[var(--lg-mint)]/50 hover:text-[var(--lg-mint)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lg-mint)]"
          aria-label={`${t("layout.switchLanguage")}: ${currentLang.name}`}
          aria-expanded={langOpen}
        >
          <LanguageFlagIcon
            code={currentLang.flagCode ?? currentLang.code}
            className="h-3 w-[18px]"
          />
          <span className="login-mono text-[10px] font-medium tracking-[0.16em]">
            {currentLang.code}
          </span>
        </button>
      </div>

      {langOpen ? (
        <div className="absolute right-0 top-full z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] rounded-2xl border border-[var(--lg-line)] bg-[var(--lg-rail)] p-3">
          <div className="grid grid-cols-2 gap-2">
            {UI_LANGUAGES.map((item) => {
              const selected = i18n.resolvedLanguage === item.lng;
              return (
                <button
                  key={item.lng}
                  type="button"
                  aria-label={item.name}
                  onClick={() => {
                    void i18n.changeLanguage(item.lng);
                    setLangOpen(false);
                  }}
                  className={[
                    "flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors duration-200",
                    selected
                      ? "bg-[var(--lg-mint-dim)] ring-2 ring-[var(--lg-mint)]/50"
                      : "hover:bg-[var(--lg-card-hover)]",
                  ].join(" ")}
                >
                  <LanguageFlagIcon
                    code={item.flagCode ?? item.code}
                    className="h-3.5 w-[21px] shrink-0"
                  />
                  <span className="min-w-0 flex flex-col">
                    <span className="truncate text-[13px] font-semibold text-[var(--lg-ink)]">
                      {item.name}
                    </span>
                    <span className="login-mono truncate text-[10px] tracking-[0.08em] text-[var(--lg-mute)]">
                      {item.sub}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
